import classNames from 'classnames';
import type { StopHeader } from '../models/stopHeader';
import styles from './StopHeader.module.scss';
import Clock from './Clock';
import { useContext, useMemo, useRef } from 'react';
import { useScaledFontSize } from '../hooks/useScaledFontSize';
import useElementSize from '../hooks/useElementSize';
import useTextWidth from '../hooks/useTextWidth';
import { BoardContext } from '@shared/contexts/BoardContext';
import useVoicedStopHeader from '@shared/hooks/useVoicedStopHeader';
import { DeepReadonly } from 'ts-essentials';
import { DepartureRow } from '@modules/departures/models/departureRow';

interface StopHeaderProps {
  header?: DeepReadonly<StopHeader>;
  showPlatforms: boolean;
  showingArrivals: boolean;
  departureRows: DeepReadonly<DepartureRow[]> | undefined;
}

export default function StopHeader({
  header,
  showPlatforms,
  showingArrivals,
  departureRows,
}: StopHeaderProps) {
  useVoicedStopHeader(header, showPlatforms, departureRows);
  const stopAreaNameRef = useRef<HTMLSpanElement>(null);
  const singleLineStopAreaName =
    header?.platforms !== undefined && header.platforms.length > 1;
  useScaledFontSize(stopAreaNameRef, {
    singleLine: singleLineStopAreaName,
  });
  const hasSinglePlatform = header?.platforms?.length === 1;
  const platformsText = header?.platforms?.join(', ');
  const boardContext = useContext(BoardContext);

  const stopAreaRef = useRef<HTMLHeadingElement>(null);
  const stopAreaSize = useElementSize(stopAreaRef);
  const stopAreaPlatformsRef = useRef<HTMLSpanElement>(null);
  const hasPlatforms = showPlatforms && platformsText !== undefined;
  const stopAreaPlatformsSize = useElementSize(stopAreaPlatformsRef, {
    renderRef: hasPlatforms,
  });

  const stopAreaName = useMemo(() => {
    if (!header?.stopAreaName) {
      return '';
    }

    let stopAreaName = header.stopAreaName;
    if (showingArrivals) {
      stopAreaName += ' (ankomster)';
    }

    return stopAreaName;
  }, [header?.stopAreaName, showingArrivals]);
  const stopAreaNameTextWidth = useTextWidth(stopAreaName, 'H3');
  const stopAreaNameExceedsSize =
    header !== undefined &&
    stopAreaPlatformsSize.width !== undefined &&
    stopAreaSize.width !== undefined &&
    stopAreaNameTextWidth + stopAreaPlatformsSize.width >= stopAreaSize.width;

  return (
    <header className={styles['stop-header']}>
      <h1
        ref={stopAreaRef}
        className={classNames(
          styles['stop-area'],
          stopAreaNameExceedsSize && styles['stop-area--multi-line'],
        )}
      >
        <span ref={stopAreaNameRef} className={styles['stop-area__name']}>
          {stopAreaName}
        </span>
        {hasPlatforms && (
          <span
            ref={stopAreaPlatformsRef}
            className={styles['stop-area__platforms']}
          >
            <span
              className={classNames(
                styles.platforms,
                hasSinglePlatform && styles['platforms--single'],
              )}
            >
              <span
                className={classNames(
                  styles.platforms__text,
                  hasSinglePlatform &&
                    platformsText.length > 1 &&
                    styles['platforms__text--multi-characters'],
                )}
              >
                {platformsText}
              </span>
            </span>
          </span>
        )}
      </h1>
      <div
        className={classNames(
          styles.clock,
          boardContext.isFirst && styles['clock--first-board'],
          boardContext.isLast && styles['clock--last-board'],
        )}
      >
        <Clock />
      </div>
    </header>
  );
}
